// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//
// V10 orchestrator for the `/v/{id}` page. The page now splits into
// three phases:
//
//   1. Existence check — public `GET /lookup/{id}`. Tells us the vault
//      is real (or not-found), without revealing any detail.
//   2. Pre-gate — for a real vault we show an ownership-gate CTA and
//      the prove flow. Nothing about the contract is rendered yet.
//   3. Unlocked — after a successful prove we fetch `GET /lookup/{id}/full`
//      with the session token and render the full vault view + the
//      gated-action panel.
//
// The whole page is gated (VD9 in stoic-severing-atlas.md) but the
// existence check stays public so the home-page search keeps working
// before any ownership proof is possible.
//

import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useRoute } from 'preact-iso';
import {
  api,
  type LookupExistsResult,
  type LookupFullResult,
  type PublicLookupResponse,
  type ChallengeResponse,
  type ProveResponse,
  type SettlementRecordResponse,
} from '../api.js';
import { mempoolAddressUrlForNetwork } from '../explorer.js';
import { formatBtc } from '../format.js';
import {
  requestSignatureFromUserApp,
  userAppOriginForCurrentVerifier,
} from '../sign-via-user-app.js';
import {
  SigningMethodChooser,
  type SigningMethod,
} from '../components/signing-method-chooser.js';
import { RecoverySignFlow } from '../components/recovery-sign-flow.js';
import { Web3AuthSignFlow } from '../components/web3auth-sign-flow.js';
import {
  HardwareSignFlow,
  connectErrorMessage,
  signErrorMessage,
} from '../components/hardware-sign-flow.js';
import {
  bsmProofFromBase64,
  deviceVaultMultisigMessagePath,
} from '@cobault/verifier-key-agent';

export function VaultLookup() {
  const { params } = useRoute();
  const [existsResult, setExistsResult] = useState<LookupExistsResult | null>(
    null,
  );
  const [session, setSession] = useState<ProveResponse | null>(null);
  // HW.12: which chooser method minted the session, so the contract-ack panel
  // can pick the matching confirmation path (hardware → on-device ceremony).
  const [sessionMethod, setSessionMethod] = useState<SigningMethod | null>(null);
  const [fullResult, setFullResult] = useState<LookupFullResult | null>(null);

  useEffect(() => {
    const id = params.id;
    if (!id) return;
    setExistsResult(null);
    setSession(null);
    setSessionMethod(null);
    setFullResult(null);
    api.lookupExists(id).then(setExistsResult);
  }, [params.id]);

  // When a session is established, fetch `/full`. Re-runs only when the
  // specific session token changes — renewing (re-proving) fetches
  // fresh data; ending (session=null) clears nothing here and the
  // branch below handles it.
  useEffect(() => {
    const id = params.id;
    if (!session || !id) return;
    let cancelled = false;
    api.lookupVaultFull(id, session.session_token).then((res) => {
      if (cancelled) return;
      if (res.kind === 'unauthorized') {
        // Token failed (expired or otherwise). Fall back to pre-gate;
        // the user can prove again.
        setSession(null);
        setFullResult(null);
        return;
      }
      setFullResult(res);
    });
    return () => {
      cancelled = true;
    };
  }, [session?.session_token, params.id]);

  const vaultId = params.id ?? '';

  if (!existsResult) {
    return <div class="lookup-loading">Looking up vault…</div>;
  }

  if (existsResult.kind === 'not-found') {
    return (
      <div class="lookup-not-found">
        <h1 class="lookup-not-found-title">Vault not found</h1>
        <p>
          The verifier has no record of a vault with this ID. The ID may
          be malformed, or the vault may have been created against a
          different verifier instance.
        </p>
        <a class="btn btn-link lookup-back-link" href="/">
          ← Back to lookup
        </a>
      </div>
    );
  }

  if (existsResult.kind === 'rate-limited') {
    return (
      <div class="lookup-error">
        <p>Too many lookups in a short window. Please wait a minute and try again.</p>
        <a class="btn btn-link lookup-back-link" href="/">
          ← Back to lookup
        </a>
      </div>
    );
  }

  if (existsResult.kind === 'error') {
    return (
      <div class="lookup-error">
        <p>Could not reach the verifier: {existsResult.message}</p>
        <a class="btn btn-link lookup-back-link" href="/">
          ← Back to lookup
        </a>
      </div>
    );
  }

  // Vault exists. Pre-gate UX until the user proves ownership.
  if (!session) {
    return (
      <PreGateShell
        vaultId={vaultId}
        derivationIndex={existsResult.value.user_derivation_index}
        onSessionEstablished={(s, m) => {
          setSession(s);
          setSessionMethod(m);
        }}
      />
    );
  }

  // Have a session; fetching `/full` is in flight or complete.
  if (!fullResult) {
    return <div class="lookup-loading">Loading vault details…</div>;
  }

  if (fullResult.kind === 'unauthorized') {
    // The effect above clears state on 'unauthorized' and we render a
    // brief transitional frame before it takes effect. Show a neutral
    // "loading" rather than a scary error while the reset races.
    return <div class="lookup-loading">Session expired. Re-proving…</div>;
  }

  if (fullResult.kind === 'rate-limited') {
    return (
      <div class="lookup-error">
        <p>Too many vault detail fetches in a short window. Wait a minute and try again.</p>
      </div>
    );
  }

  if (fullResult.kind === 'not-found') {
    return (
      <div class="lookup-error">
        <p>The verifier no longer has a record of this vault.</p>
      </div>
    );
  }

  if (fullResult.kind === 'error') {
    return (
      <div class="lookup-error">
        <p>Could not load vault details: {fullResult.message}</p>
      </div>
    );
  }

  const resetSession = () => {
    setSession(null);
    setSessionMethod(null);
    setFullResult(null);
  };

  return (
    <>
      {/* COB-245/COB-264: independent contract confirmation. Folded into the
          "Vault status" section near the top (was a standalone section below
          the full contract dump → below the fold) so the load-bearing confirm
          — user-app withholds the deposit address until it is done — is
          obvious the moment the vault loads. The full contract view stays
          below for review. */}
      <VaultLookupView
        data={fullResult.value}
        confirmSlot={
          <ConfirmContractPanel
            vaultId={vaultId}
            session={session}
            contractHashHex={fullResult.value.contract_hash_hex}
            hardware={sessionMethod === 'hardware'}
            derivationIndex={fullResult.value.user_derivation_index}
            onSessionExpired={resetSession}
          />
        }
      />
      <GatedActionsPanel vaultId={vaultId} session={session} onLock={resetSession} />
    </>
  );
}

/** Pre-gate view shown once the verifier confirms the vault exists,
 *  before any ownership proof. Shows the vault ID, a brief framing
 *  paragraph, and the V11 signing-method chooser. Contract details
 *  are not rendered here. */
function PreGateShell({
  vaultId,
  derivationIndex,
  onSessionEstablished,
}: {
  vaultId: string;
  derivationIndex: number;
  onSessionEstablished: (session: ProveResponse, method: SigningMethod) => void;
}) {
  const [method, setMethod] = useState<SigningMethod | null>(null);

  // Inject the chosen method into every flow's session callback so the parent
  // knows which path minted the session (HW.12: the ack panel branches on it).
  const establish = (session: ProveResponse) => {
    if (method !== null) onSessionEstablished(session, method);
  };

  return (
    <>
      <header class="lookup-header">
        <div>
          <div class="lookup-title">Vault</div>
          <div class="lookup-vault-id">{vaultId}</div>
        </div>
        <span class="lookup-status-pill" data-state="locked">
          locked
        </span>
      </header>

      <section class="lookup-section">
        <h2>Prove ownership to view details</h2>
        <p class="stage2-blurb">
          This page shows the verifier's record of the vault: contract
          hash, addresses, per-vault multisig pubkey, and — once
          settled — the signed settlement record. Choose how you'd
          like to prove you own this vault. The Cobault app is the
          normal choice; recovery is for when the Cobault app is
          unavailable.
        </p>

        {method === null && <SigningMethodChooser onChoose={setMethod} />}

        {method !== null && (
          <div class="chooser-selection">
            <button
              type="button"
              class="btn btn-link chooser-change-method"
              onClick={() => setMethod(null)}
            >
              ← Change method
            </button>
            {/* cobault-app rides the V08b cross-origin popup — the user picks
                their login (self-custody, Web3Auth, or a Ledger connected in
                the popup) there, and `ProveFlow` routes the reply by format
                (DER → /prove, WS-H proof → /prove-v2). */}
            {method === 'cobault-app' && (
              <ProveFlow
                vaultId={vaultId}
                onSessionEstablished={establish}
              />
            )}
            {/* HW.12: the dedicated hardware tile signs NATIVELY in this origin
                over WebHID — no popup, no user-app dependency (arm's-length). */}
            {method === 'hardware' && (
              <HardwareSignFlow
                vaultId={vaultId}
                derivationIndex={derivationIndex}
                onSessionEstablished={establish}
                onCancel={() => setMethod(null)}
              />
            )}
            {method === 'recovery' && (
              <RecoverySignFlow
                vaultId={vaultId}
                derivationIndex={derivationIndex}
                onSessionEstablished={establish}
                onCancel={() => setMethod(null)}
              />
            )}
            {method === 'web3auth' && (
              <Web3AuthSignFlow
                vaultId={vaultId}
                derivationIndex={derivationIndex}
                onSessionEstablished={establish}
                onCancel={() => setMethod(null)}
              />
            )}
          </div>
        )}
      </section>
    </>
  );
}

/** Normalise the verifier's state wire value to the user-visible label.
 *  The state-rename forward-compat window accepts `"finalized"` from
 *  any old binary still in flight; we always display "active" to avoid
 *  confusing users with the legacy word. */
function stateLabel(state: string): string {
  if (state === 'finalized') return 'active';
  return state;
}

/** Deposit-address text, linked out to its mempool.space address page
 *  on mainnet only (plain text on every other network). */
function DepositAddressValue({
  address,
  network,
}: {
  address: string;
  network: string;
}) {
  const explorerUrl = mempoolAddressUrlForNetwork(network, address);
  if (!explorerUrl) return <>{address}</>;
  return (
    <a
      class="lookup-address-link"
      href={explorerUrl}
      target="_blank"
      rel="noopener"
    >
      {address}
    </a>
  );
}

function VaultLookupView({
  data,
  confirmSlot,
}: {
  data: PublicLookupResponse;
  confirmSlot?: ComponentChildren;
}) {
  const { contract, status } = data;
  // Accept both `activated_at_unix` (new) and `finalized_at_unix` (legacy).
  const activatedAtUnix =
    status.activated_at_unix ?? status.finalized_at_unix ?? null;

  return (
    <>
      <header class="lookup-header">
        <div>
          <div class="lookup-title">Vault</div>
          <div class="lookup-vault-id">{data.vault_id}</div>
          <dl class="lookup-subtitle">
            <div class="lookup-subtitle-row">
              <dt>Deposit</dt>
              <dd class="mono">
                <DepositAddressValue
                  address={data.deposit_address}
                  network={data.bitcoin_network}
                />
              </dd>
            </div>
            <div class="lookup-subtitle-row">
              <dt>Payout</dt>
              <dd>{contract.loss_formula}</dd>
            </div>
            <div class="lookup-subtitle-row">
              <dt>Type</dt>
              <dd>BTC yield by Cobault ProductCo</dd>
            </div>
          </dl>
        </div>
        <span class="lookup-status-pill" data-state={status.state}>
          {stateLabel(status.state)}
        </span>
      </header>

      <section class="lookup-hash-block">
        <div class="lookup-hash-block-label">Contract hash</div>
        <div class="lookup-hash-block-value">{data.contract_hash_hex}</div>
        <p class="lookup-hash-block-help">
          Compare this hash to the one your user-app shows for this
          vault. If they match, both sides agree on the contract. If
          they differ, do not transact — something has tampered with
          one of the two views.
        </p>
      </section>

      <section class="lookup-section">
        <h2>Vault status (verifier view)</h2>
        <div class="lookup-fields">
          <div class="lookup-field-label">State</div>
          <div class="lookup-field-value">{stateLabel(status.state)}</div>

          <div class="lookup-field-label">Created</div>
          <div class="lookup-field-value">
            {formatUnix(status.created_at_unix)}
          </div>

          {activatedAtUnix !== null && (
            <>
              <div class="lookup-field-label">Activated</div>
              <div class="lookup-field-value">
                {formatUnix(activatedAtUnix)}
              </div>
            </>
          )}

          {status.aborted_at_unix !== null && (
            <>
              <div class="lookup-field-label">Aborted</div>
              <div class="lookup-field-value">
                {formatUnix(status.aborted_at_unix)}
              </div>
            </>
          )}

          <div class="lookup-field-label">Settlement records</div>
          <div class="lookup-field-value">{status.settlement_record_count}</div>

          {status.latest_settlement_observed_at_unix !== null && (
            <>
              <div class="lookup-field-label">Latest observation</div>
              <div class="lookup-field-value">
                {formatUnix(status.latest_settlement_observed_at_unix)}
              </div>
            </>
          )}

          {/* COB-264: the contract-confirmation control lives here, at the
              foot of the status grid, so it is the load-bearing thing the
              owner sees with the vault's status — not buried below the full
              contract dump. Spans the full grid width (see .lookup-confirm-row). */}
          {confirmSlot}
        </div>
      </section>

      <section class="lookup-section">
        <h2>Verifier keys</h2>
        <div class="lookup-fields">
          <div class="lookup-field-label">Per-vault multisig pubkey</div>
          <div class="lookup-field-value mono">
            {data.verifier_multisig_pubkey_hex}
          </div>

          <div class="lookup-field-label">Identity pubkey</div>
          <div class="lookup-field-value mono">
            {data.verifier_identity_pubkey_hex}
          </div>
        </div>
      </section>

      <section class="lookup-section">
        <h2>Contract terms</h2>
        <div class="lookup-fields">
          <div class="lookup-field-label">Schema version</div>
          <div class="lookup-field-value">{contract.schema_version}</div>

          <div class="lookup-field-label">Principal (BTC)</div>
          <div class="lookup-field-value tabular-nums">
            {formatBtc(contract.principal_sats)}
          </div>

          <div class="lookup-field-label">Reward (BTC)</div>
          <div class="lookup-field-value tabular-nums">
            {formatBtc(contract.reward_sats)}
          </div>

          <div class="lookup-field-label">Max loss (bps)</div>
          <div class="lookup-field-value tabular-nums">
            {contract.max_loss_bps}
          </div>

          <div class="lookup-field-label">Loss formula</div>
          <div class="lookup-field-value mono">{contract.loss_formula}</div>

          <div class="lookup-field-label">Strikes (USD)</div>
          <div class="lookup-field-value">
            short {contract.strikes.short_usd.toLocaleString()} / long{' '}
            {contract.strikes.long_usd.toLocaleString()}
          </div>

          <div class="lookup-field-label">Term start block</div>
          <div class="lookup-field-value tabular-nums">
            {contract.term_start_block.toLocaleString()}
          </div>

          <div class="lookup-field-label">Term end block</div>
          <div class="lookup-field-value tabular-nums">
            {contract.term_end_block.toLocaleString()}
          </div>

          <div class="lookup-field-label">Refund unlock block</div>
          <div class="lookup-field-value tabular-nums">
            {contract.refund_unlock_block.toLocaleString()}
          </div>

          <div class="lookup-field-label">Settlement reference time</div>
          <div class="lookup-field-value">
            {formatUnix(contract.settlement_reference_time_unix)}
          </div>

          <div class="lookup-field-label">Price source</div>
          <div class="lookup-field-value mono">{contract.price_source_id}</div>
        </div>
      </section>

      <section class="lookup-section">
        <h2>Addresses & descriptor</h2>
        <div class="lookup-fields">
          <div class="lookup-field-label">Deposit address</div>
          <div class="lookup-field-value mono">
            <DepositAddressValue
              address={data.deposit_address}
              network={data.bitcoin_network}
            />
          </div>

          <div class="lookup-field-label">User payout address</div>
          <div class="lookup-field-value mono">
            {contract.user_payout_address}
          </div>

          <div class="lookup-field-label">Platform refund address</div>
          <div class="lookup-field-value mono">
            {contract.platform_refund_address}
          </div>

          <div class="lookup-field-label">Multisig descriptor</div>
          <div class="lookup-field-value mono">
            {contract.multisig_descriptor}
          </div>
        </div>
      </section>

      <section class="lookup-section">
        <h2>Signed receipt (D5)</h2>
        <div class="lookup-fields">
          <div class="lookup-field-label">Signature (base64)</div>
          <div class="lookup-field-value mono">
            {data.signed_receipt.signature_base64}
          </div>
          <div class="lookup-field-label">Schema version</div>
          <div class="lookup-field-value">
            {data.signed_receipt.receipt.schema_version}
          </div>
          <div class="lookup-field-label">Receipt created</div>
          <div class="lookup-field-value">
            {formatUnix(data.signed_receipt.receipt.created_at_unix)}
          </div>
        </div>
      </section>
    </>
  );
}

// ── V05 + V08b ownership prove flow ─────────────────────────────────
//
// Extracted from the old Stage2Panel so that it can drive either the
// pre-gate shell (V10) or any future chooser tile. Click flow:
//   1. Request a one-shot challenge nonce (V05).
//   2. Open the Cobault user-app at its `/sign-verifier-challenge`
//      page in a popup (V08b). The user-app signs the canonical
//      message and postMessages the DER signature back.
//   3. POST `{nonce, signature}` to /prove → session token.
//   4. Hand the session up to the parent via `onSessionEstablished`.

interface ProveState {
  challenge: ChallengeResponse | null;
  error: string | null;
  fallbackUrl: string | null;
  busy: boolean;
}

const INITIAL_PROVE: ProveState = {
  challenge: null,
  error: null,
  fallbackUrl: null,
  busy: false,
};

function ProveFlow({
  vaultId,
  onSessionEstablished,
}: {
  vaultId: string;
  onSessionEstablished: (session: ProveResponse) => void;
}) {
  const [state, setState] = useState<ProveState>(INITIAL_PROVE);
  const userAppOrigin = userAppOriginForCurrentVerifier(
    typeof window !== 'undefined' ? window.location.origin : '',
  );

  async function ensureChallenge(): Promise<ChallengeResponse | null> {
    if (state.challenge) return state.challenge;
    setState((s) => ({ ...s, busy: true, error: null }));
    const challenge = await api.issueChallenge(vaultId);
    if (!challenge) {
      setState({
        ...INITIAL_PROVE,
        error: 'Could not start ownership challenge. Try again in a minute.',
      });
      return null;
    }
    setState((s) => ({
      ...s,
      challenge,
      busy: false,
      error: null,
      fallbackUrl: null,
    }));
    return challenge;
  }

  async function signViaPopup() {
    const challenge = await ensureChallenge();
    if (!challenge) return;
    setState((s) => ({ ...s, busy: true, error: null, fallbackUrl: null }));

    const popupResult = await requestSignatureFromUserApp({
      vault: vaultId,
      nonce: challenge.nonce_hex,
    });

    if (popupResult.kind === 'popup-blocked') {
      setState((s) => ({
        ...s,
        busy: false,
        error:
          'Your browser blocked the Cobault user-app popup. Use the link below to open it in a new tab and finish signing there.',
        fallbackUrl: popupResult.fallbackUrl,
      }));
      return;
    }
    if (popupResult.kind === 'not-configured') {
      setState((s) => ({
        ...s,
        busy: false,
        error:
          'Cross-origin signing is not configured for this verifier host. Contact the operator.',
      }));
      return;
    }
    if (popupResult.kind === 'timeout') {
      setState((s) => ({
        ...s,
        busy: false,
        error:
          'Timed out waiting for a signature from the Cobault user-app. Try again.',
      }));
      return;
    }
    if (popupResult.kind === 'popup-closed') {
      setState((s) => ({
        ...s,
        busy: false,
        error: 'The Cobault user-app window closed before signing finished.',
      }));
      return;
    }
    if (popupResult.kind === 'aborted') {
      setState((s) => ({ ...s, busy: false }));
      return;
    }

    // popupResult is 'ok' (a bare DER hex from a software / Web3Auth popup
    // session) or 'ok-proof' (a WS-H proof object from a hardware session —
    // a Ledger cannot emit the raw DER). HW.11: route each to its matching
    // prove endpoint; session-establishment + error handling is identical.
    const result =
      popupResult.kind === 'ok-proof'
        ? await api.proveV2(vaultId, challenge.nonce_hex, popupResult.proof)
        : await api.prove(
            vaultId,
            challenge.nonce_hex,
            popupResult.signatureDerHex,
          );
    if (result.kind === 'ok') {
      // Reset local state so a subsequent re-prove starts fresh.
      setState(INITIAL_PROVE);
      onSessionEstablished(result.value);
      return;
    }
    let message: string;
    switch (result.kind) {
      case 'invalid-signature':
        message =
          'Signature did not verify against this vault’s user key. Are you signing with the right device?';
        break;
      case 'expired-or-used-nonce':
        message =
          'This challenge has expired or been used. Click Sign to request a new one.';
        // Drop the stale challenge so the next click starts fresh.
        setState((s) => ({
          ...s,
          challenge: null,
          busy: false,
          error: message,
        }));
        return;
      case 'rate-limited':
        message = 'Too many attempts. Wait a minute and try again.';
        break;
      default:
        message = result.message ?? 'Unknown error';
    }
    setState((s) => ({ ...s, busy: false, error: message }));
  }

  return (
    <div class="stage2-prove">
      {state.challenge && (
        <div class="stage2-field">
          <label>Canonical message (signed verbatim)</label>
          <code class="stage2-canonical">
            {state.challenge.canonical_message}
          </code>
          <p class="stage2-help">
            Click <strong>Sign at user-app</strong> to open the Cobault
            user-app in a popup and sign this message with your vault's
            user multisig key. The signature is posted back to this page
            automatically — nothing is stored in this origin.
          </p>
          {userAppOrigin && (
            <p class="stage2-help">
              The popup will load{' '}
              <code class="mono">{userAppOrigin}/sign-verifier-challenge</code>.
              If you aren't signed in to Cobault yet, you'll be
              prompted there first.
            </p>
          )}
        </div>
      )}
      {state.error && (
        <div class="stage2-error" role="alert">
          {state.error}
          {state.fallbackUrl && (
            <>
              {' '}
              <a href={state.fallbackUrl} target="_blank" rel="noopener">
                Open user-app sign page ↗
              </a>
            </>
          )}
        </div>
      )}
      <div class="stage2-actions">
        <button
          class="btn btn-primary"
          onClick={signViaPopup}
          disabled={state.busy || !userAppOrigin}
        >
          {state.busy
            ? 'Waiting for signature…'
            : state.challenge
              ? 'Sign at user-app'
              : 'Prove ownership'}
        </button>
      </div>
    </div>
  );
}

// ── COB-245 / COB-264: independent contract confirmation ──────────
//
// After the owner proves ownership and reviews the verifier's
// independently-stored contract, they confirm it here. The confirmation
// is recorded against the exact stored contract hash and is the
// precondition user-app checks before it reveals the deposit address (and
// the verifier checks before it activates the vault) — so a
// compromised-SPA-registered malicious payout/refund address is caught on
// this independent surface before any funds move.
//
// COB-264: this control is rendered as a full-width row at the foot of the
// "Vault status" section (see VaultLookupView) rather than as a standalone
// section below the whole contract dump, where it fell below the fold and
// could be scrolled past. The full contract terms remain on the page below
// for review; the copy points the owner to them.

function ConfirmContractPanel({
  vaultId,
  session,
  contractHashHex,
  hardware,
  derivationIndex,
  onSessionExpired,
}: {
  vaultId: string;
  session: ProveResponse;
  contractHashHex: string;
  /** HW.12: the session was minted on a Ledger — confirm via the on-device
   *  contract-ack ceremony (`/ack-challenge` → BSM-sign → `/ack-v2`) instead of
   *  the session-gated `/ack`. The device shows the contract hash on-screen
   *  (WYSIWYS), so possession of the session alone can never ack (closes review
   *  finding A3). */
  hardware: boolean;
  /** Per-vault device account counter, for the on-device signing path. */
  derivationIndex: number;
  onSessionExpired: () => void;
}) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'connecting' }
    | { kind: 'signing' }
    | { kind: 'confirmed'; atUnix: number }
    | { kind: 'conflict'; message: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Session-gated confirmation (software / Web3Auth / recovery sessions).
  async function confirmViaSession() {
    setState({ kind: 'busy' });
    const result = await api.acknowledgeContract(
      vaultId,
      session.session_token,
      contractHashHex,
    );
    if (result.kind === 'ok') {
      setState({ kind: 'confirmed', atUnix: result.value.acked_at_unix });
      return;
    }
    if (result.kind === 'unauthorized') {
      // Token expired between pages — drop back to pre-gate so the owner
      // can re-prove, matching the gated-actions panel's behaviour.
      onSessionExpired();
      return;
    }
    if (result.kind === 'conflict') {
      setState({ kind: 'conflict', message: result.message });
      return;
    }
    const message =
      result.kind === 'rate-limited'
        ? 'Rate-limited — wait a minute and try again.'
        : result.kind === 'not-found'
          ? 'The verifier no longer has a record of this vault.'
          : result.message;
    setState({ kind: 'error', message });
  }

  // HW.12: on-device contract-ack ceremony for a hardware session. The device
  // signs the verifier's own canonical ack message (which binds the stored hash
  // + network); `/ack-v2` re-verifies against the vault's user key. Not
  // session-gated — the fresh on-device signature is the authority.
  async function confirmOnDevice() {
    setState({ kind: 'connecting' });
    let conn: Awaited<
      ReturnType<typeof import('../hardware-connect.js')['connectLedger']>
    >;
    try {
      const { connectLedger } = await import('../hardware-connect.js');
      conn = await connectLedger();
    } catch (err) {
      setState({ kind: 'error', message: connectErrorMessage(err) });
      return;
    }
    try {
      setState({ kind: 'signing' });
      const ceremony = await api.ackChallenge(vaultId);
      if (!ceremony) {
        setState({
          kind: 'error',
          message: 'Could not start the on-device confirmation. Try again in a minute.',
        });
        return;
      }
      // The verifier binds its OWN stored hash into the message. If it differs
      // from the hash the owner reviewed on this page, the contract changed —
      // do not sign a hash the human did not confirm.
      if (ceremony.contract_hash_hex !== contractHashHex) {
        setState({
          kind: 'conflict',
          message: 'stored contract hash changed since you loaded this page',
        });
        return;
      }
      let proof;
      try {
        const signatureBase64 = await conn.signMessage(
          deviceVaultMultisigMessagePath(conn.network, derivationIndex),
          ceremony.canonical_message,
        );
        proof = bsmProofFromBase64(signatureBase64);
      } catch (err) {
        setState({ kind: 'error', message: signErrorMessage(err) });
        return;
      }
      const result = await api.ackV2(vaultId, ceremony.nonce_hex, proof);
      if (result.kind === 'ok') {
        setState({ kind: 'confirmed', atUnix: result.value.acked_at_unix });
        return;
      }
      let message: string;
      switch (result.kind) {
        case 'invalid-signature':
          message =
            "The device signature didn't verify — check you connected the right Ledger and opened the matching network app, then try again.";
          break;
        case 'bad-proof':
          message =
            'The device produced an unexpected signature. Reconnect the Ledger and try again.';
          break;
        case 'rate-limited':
          message = 'Rate-limited — wait a minute and try again.';
          break;
        case 'not-found':
          message = 'The verifier no longer has a record of this vault.';
          break;
        default:
          message = result.message;
      }
      setState({ kind: 'error', message });
    } finally {
      await conn.close().catch(() => undefined);
    }
  }

  const confirm = hardware ? confirmOnDevice : confirmViaSession;
  const busy =
    state.kind === 'busy' ||
    state.kind === 'connecting' ||
    state.kind === 'signing';
  const buttonLabel =
    state.kind === 'connecting'
      ? 'Connecting…'
      : state.kind === 'signing'
        ? 'Approve on device…'
        : state.kind === 'busy'
          ? 'Confirming…'
          : hardware
            ? 'Confirm on device'
            : 'Confirm these terms';

  return (
    <div class="lookup-confirm-row">
      <div class="lookup-confirm-label">Contract confirmation</div>
      {state.kind === 'confirmed' ? (
        <div class="lookup-confirm-done">
          <span class="stage2-checkmark">✓</span>
          <span>
            You confirmed these terms on {formatUnix(state.atUnix)}. You can now
            return to the app to reveal your deposit address.
          </span>
        </div>
      ) : (
        <>
          <p class="confirm-intro">
            You are viewing the contract exactly as the independent verifier
            stored it — not as the app showed it to you. Check that the deposit
            amount, reward, price levels, and the payout and refund addresses
            shown below are what you agreed to, then confirm. Your deposit
            address is revealed only after you confirm.
            {hardware
              ? ' Your Ledger will display the contract hash — approve it on the device to confirm.'
              : ''}
          </p>
          <div class="lookup-confirm-actions">
            <span class="lookup-confirm-pill" data-state="needed">
              Confirmation needed
            </span>
            <button
              class="btn btn-primary"
              onClick={confirm}
              disabled={busy}
              data-testid={hardware ? 'confirm-on-device' : 'confirm-session'}
            >
              {buttonLabel}
            </button>
          </div>
          {state.kind === 'conflict' && (
            <p class="stage2-result stage2-result-error">
              The contract changed since you loaded this page. Refresh to review
              the updated terms, then confirm again. ({state.message})
            </p>
          )}
          {state.kind === 'error' && (
            <p class="stage2-result stage2-result-error">{state.message}</p>
          )}
        </>
      )}
    </div>
  );
}

// ── V07 gated actions (and future V07b Phase-5 refund) ─────────────
//
// Post-V10 this panel is always rendered below an unlocked vault view,
// so it no longer needs its own idle/open/unlocked state machine.

function GatedActionsPanel({
  vaultId,
  session,
  onLock,
}: {
  vaultId: string;
  session: ProveResponse;
  onLock: () => void;
}) {
  const [recordResult, setRecordResult] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'ok'; record: SettlementRecordResponse }
    | { kind: 'not-yet' }
    | { kind: 'unauthorized' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function downloadRecord() {
    setRecordResult({ kind: 'busy' });
    const result = await api.downloadSettlementRecord(
      vaultId,
      session.session_token,
    );
    if (result.kind === 'ok') {
      const blob = new Blob([JSON.stringify(result.value, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cobault-vault-${vaultId}-settlement.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setRecordResult({ kind: 'ok', record: result.value });
      return;
    }
    if (result.kind === 'unauthorized') {
      // Token expired between pages. Drop back to pre-gate so the user
      // can re-prove.
      setRecordResult({ kind: 'unauthorized' });
      onLock();
      return;
    }
    if (result.kind === 'not-yet-settled') {
      setRecordResult({ kind: 'not-yet' });
      return;
    }
    setRecordResult({
      kind: 'error',
      message: result.kind === 'rate-limited' ? 'Rate-limited' : result.message,
    });
  }

  return (
    <section class="lookup-section">
      <h2>Advanced — verified actions</h2>
      <div class="stage2-card">
        <div class="stage2-unlocked-header">
          <span class="stage2-checkmark">✓</span>
          <span>
            Ownership verified — session active until{' '}
            {formatUnix(session.expires_at_unix)}
          </span>
          <button class="btn btn-link" onClick={onLock}>
            End session
          </button>
        </div>

        <div class="stage2-actions stage2-actions-grid">
          <div>
            <div class="stage2-action-title">
              <button
                class="btn btn-primary"
                onClick={downloadRecord}
                disabled={recordResult.kind === 'busy'}
              >
                {recordResult.kind === 'busy'
                  ? 'Downloading…'
                  : 'Download signed settlement record'}
              </button>
              <span
                class="stage2-info-dot"
                role="img"
                aria-label="What is this?"
                title={
                  'A JSON artefact the verifier Ed25519-signs at term end: ' +
                  'contract hash, the Deribit settlement price observed, ' +
                  'and the resulting user/platform sat split. You can hand ' +
                  'it to a third party (auditor, tax advisor, insurer, ' +
                  'court) to prove Cobault computed the settlement ' +
                  'correctly against an independent price feed. Verify ' +
                  "the signature against the verifier's pinned identity " +
                  'public key shown above.'
                }
              >
                (i)
              </span>
            </div>
            {recordResult.kind === 'not-yet' && (
              <p class="stage2-result">
                The verifier hasn't observed a settlement for this vault
                yet. Try again after the term ends.
              </p>
            )}
            {recordResult.kind === 'unauthorized' && (
              <p class="stage2-result stage2-result-error">
                Session expired. Re-prove to continue.
              </p>
            )}
            {recordResult.kind === 'error' && (
              <p class="stage2-result stage2-result-error">
                {recordResult.message}
              </p>
            )}
            {recordResult.kind === 'ok' && (
              <p class="stage2-result">
                Downloaded. The file is also dumped below for your
                reference.
              </p>
            )}
          </div>

          <div>
            <button class="btn btn-secondary" disabled aria-disabled="true">
              Recover any outstanding user funds (coming soon)
            </button>
            <p class="stage2-result">
              Break-glass lever for when the main user-app withdrawal path
              is unavailable. Requests the platform to pay out whatever
              you're owed for this vault, using the verifier's 2-of-3
              multisig authority. Not yet wired — tracked as a follow-up.
            </p>
          </div>
        </div>

        {recordResult.kind === 'ok' && (
          <pre class="stage2-record-dump">
            {JSON.stringify(recordResult.record, null, 2)}
          </pre>
        )}
      </div>
    </section>
  );
}

function formatUnix(seconds: number): string {
  if (!seconds) return '—';
  const date = new Date(seconds * 1000);
  return `${date.toISOString().replace('T', ' ').replace(/\..*/, '')} UTC`;
}
